import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Briefcase, Loader2 } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { COMPANY, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-[#27272A] focus:border-[#C9A24B] focus:outline-none focus:ring-0 px-0 py-3 text-[#F5F5F0] placeholder:text-[#52525B] transition-colors";
const labelCls = "block text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-2";

const initialEnquiry = { name: "", email: "", phone: "", service: "", message: "", consent: false };
const initialRecruit = { name: "", email: "", phone: "", position: "", message: "", consent: false };

export default function Contact() {
  const [tab, setTab] = useState("enquiry");
  const [enquiry, setEnquiry] = useState(initialEnquiry);
  const [recruit, setRecruit] = useState(initialRecruit);
  const [submitting, setSubmitting] = useState(false);

  const isEnquiry = tab === "enquiry";
  const form = isEnquiry ? enquiry : recruit;
  const setForm = isEnquiry ? setEnquiry : setRecruit;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to proceed.");
      return;
    }
    setSubmitting(true);
    const payload = {
      type: tab,
      name: form.name,
      email: form.email,
      phone: form.phone || null,
      message: form.message,
      consent: form.consent,
      ...(isEnquiry ? { service: form.service || null } : { position: form.position || null }),
    };
    try {
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you. Your message has been received — we will be in touch in confidence.");
      setForm(isEnquiry ? initialEnquiry : initialRecruit);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly at " + COMPANY.email);
    } finally {
      setSubmitting(false);
    }
  };

  const mailtoSubject = isEnquiry ? "General Enquiry" : "Recruitment Enquiry";
  const mailtoHref = `mailto:${COMPANY.email}?subject=${encodeURIComponent(mailtoSubject)}`;

  return (
    <div data-testid="contact-page">
      <section className="pt-40 pb-16 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <SectionLabel className="mb-6">Get in touch</SectionLabel>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F0] max-w-3xl">
              Contact Us
            </h1>
            <p className="mt-6 text-lg text-[#A1A1AA] font-light max-w-2xl">
              For client enquiries or recruitment, reach our team in confidence. All correspondence is
              handled with the utmost discretion.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-28 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-16">
          {/* Direct contact */}
          <Reveal className="lg:col-span-2">
            <div className="space-y-10">
              <div>
                <h2 className="font-serif text-2xl text-[#F5F5F0] mb-6">Direct contact</h2>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <span className="flex items-center justify-center w-11 h-11 border border-[#27272A] rounded-full shrink-0">
                      <Mail size={18} strokeWidth={1.4} className="text-[#C9A24B]" />
                    </span>
                    <div>
                      <p className={labelCls}>Email</p>
                      <a data-testid="contact-email-link" href={`mailto:${COMPANY.email}`} className="text-[#F5F5F0] hover:text-[#C9A24B] transition-colors break-all">
                        {COMPANY.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex items-center justify-center w-11 h-11 border border-[#27272A] rounded-full shrink-0">
                      <Phone size={18} strokeWidth={1.4} className="text-[#C9A24B]" />
                    </span>
                    <div>
                      <p className={labelCls}>Telephone</p>
                      <a href={COMPANY.phoneHref} className="text-[#F5F5F0] hover:text-[#C9A24B] transition-colors">
                        {COMPANY.phone}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex items-center justify-center w-11 h-11 border border-[#27272A] rounded-full shrink-0">
                      <MapPin size={18} strokeWidth={1.4} className="text-[#C9A24B]" />
                    </span>
                    <div>
                      <p className={labelCls}>Location</p>
                      <span className="text-[#F5F5F0]">{COMPANY.location}</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="border border-[#27272A] p-6 bg-[#121214]">
                <p className="text-[#A1A1AA] font-light leading-relaxed text-sm">
                  Prefer email? Write to us directly at{" "}
                  <a href={mailtoHref} className="text-[#C9A24B] hover:underline break-all">{COMPANY.email}</a>.
                  We treat every enquiry with strict confidentiality.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Form with tabs */}
          <Reveal className="lg:col-span-3" delay={0.1}>
            <div className="bg-[#121214] border border-[#27272A] p-8 sm:p-10">
              <div className="grid grid-cols-2 gap-2 mb-10 p-1 border border-[#27272A] rounded-sm">
                <button
                  data-testid="tab-enquiry"
                  onClick={() => setTab("enquiry")}
                  className={`flex items-center justify-center gap-2 py-3 text-xs tracking-[0.15em] uppercase transition-colors ${
                    isEnquiry ? "bg-[#C9A24B] text-black" : "text-[#A1A1AA] hover:text-[#F5F5F0]"
                  }`}
                >
                  <Mail size={16} strokeWidth={1.5} /> Enquiry
                </button>
                <button
                  data-testid="tab-recruitment"
                  onClick={() => setTab("recruitment")}
                  className={`flex items-center justify-center gap-2 py-3 text-xs tracking-[0.15em] uppercase transition-colors ${
                    !isEnquiry ? "bg-[#C9A24B] text-black" : "text-[#A1A1AA] hover:text-[#F5F5F0]"
                  }`}
                >
                  <Briefcase size={16} strokeWidth={1.5} /> Recruitment
                </button>
              </div>

              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className={labelCls}>Full name *</label>
                    <input
                      data-testid="contact-name-input"
                      required minLength={2}
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className={inputCls}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input
                      data-testid="contact-email-input"
                      required type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className={inputCls}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className={inputCls}
                      placeholder="Optional"
                    />
                  </div>
                  {isEnquiry ? (
                    <div>
                      <label className={labelCls}>Service of interest</label>
                      <select
                        data-testid="contact-service-select"
                        value={form.service}
                        onChange={(e) => update("service", e.target.value)}
                        className={`${inputCls} appearance-none cursor-pointer`}
                      >
                        <option value="" className="bg-[#121214]">Select a service</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.title} className="bg-[#121214]">{s.title}</option>
                        ))}
                        <option value="Other / Bespoke" className="bg-[#121214]">Other / Bespoke</option>
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className={labelCls}>Position of interest</label>
                      <input
                        data-testid="contact-position-input"
                        value={form.position}
                        onChange={(e) => update("position", e.target.value)}
                        className={inputCls}
                        placeholder="e.g. Close Protection Officer"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelCls}>
                    {isEnquiry ? "How can we help? *" : "Tell us about yourself *"}
                  </label>
                  <textarea
                    data-testid="contact-message-input"
                    required minLength={10} rows={4}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className={`${inputCls} resize-none`}
                    placeholder={isEnquiry ? "Briefly describe your requirements..." : "Experience, background and relevant qualifications..."}
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="contact-consent-checkbox"
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#C9A24B] shrink-0"
                  />
                  <span className="text-sm text-[#A1A1AA] font-light leading-relaxed">
                    I consent to Blackthorne Stratton processing my data to respond to my enquiry, in line
                    with the <a href="/privacy-policy" className="text-[#C9A24B] hover:underline">Privacy Policy</a>.
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C9A24B] text-black px-10 py-4 text-sm tracking-[0.12em] uppercase hover:bg-[#E5E4E2] transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 size={18} className="animate-spin" /> Sending…</>
                  ) : (
                    <>Send message <Send size={16} className="group-hover:translate-x-1 transition-transform" /></>
                  )}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
